import React from "react";
import { BsChevronRight, BsChevronDown } from "react-icons/bs";

/**
 * Shared CollapsibleSection component for consistent collapsible panel UI
 * Used by VepForm and other components that need collapsible sections
 *
 * @param {string} title - Section title to display in the header
 * @param {boolean} isCollapsed - Whether the section is currently collapsed
 * @param {Function} onToggle - Function to call when the section is toggled
 * @param {React.ReactNode} children - Content to display when expanded
 * @param {string} className - Additional CSS classes for the container
 * @param {string} headerClassName - Additional CSS classes for the header
 * @param {Object} headerStyle - Inline styles for the header
 * @param {boolean} showIcon - Whether to show a collapse/expand icon
 * @param {string} variant - Card variant: 'default', 'flush', 'shadow'
 */
const CollapsibleSection = ({
  title,
  isCollapsed,
  onToggle,
  children,
  className = "",
  headerClassName = "",
  headerStyle = {},
  showIcon = true,
  variant = "default",
}) => {
  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const getIcon = () => {
    if (!showIcon) return null;
    return isCollapsed ? (
      <BsChevronRight className="transition-all" />
    ) : (
      <BsChevronDown className="transition-all" />
    );
  };

  const getCardClasses = () => {
    const baseClasses = "card mb-3";
    switch (variant) {
      case "flush":
        return `${baseClasses} border-0`;
      case "shadow":
        return `${baseClasses} border-0 shadow-sm`;
      case "active":
        return `${baseClasses} border-0 shadow-sm collapsible-section-active`;
      default:
        return `${baseClasses} border-0 shadow-sm`;
    }
  };

  return (
    <div
      className={`${getCardClasses()} ${className}`}
      style={{ borderRadius: "0.5rem" }}
    >
      <div
        className={`card-header border-0 py-3 ${headerClassName}`}
        style={{
          cursor: "pointer",
          borderRadius: isCollapsed ? "0.5rem" : "0.5rem 0.5rem 0 0",
          ...headerStyle,
        }}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-expanded={!isCollapsed}
        aria-controls={`collapsible-content-${title
          ?.replace(/\s+/g, "-")
          ?.toLowerCase()}`}
      >
        <div className="d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-semibold">{title}</h6>
          {getIcon()}
        </div>
      </div>

      <div
        id={`collapsible-content-${title?.replace(/\s+/g, "-")?.toLowerCase()}`}
        className={`collapse ${isCollapsed ? "" : "show"}`}
      >
        <div
          className="card-body pt-0"
          style={{ borderRadius: "0 0 0.5rem 0.5rem" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
